import { useFormContext } from 'react-hook-form';
import { CharacterCounter } from '@/components/common/CharacterCounter';
import { DatePickerInput } from '@/components/common/DatePickerInput';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  SURVEY_DESCRIPTION_MAX_LENGTH,
  SURVEY_TITLE_MAX_LENGTH,
} from '../../constants/survey.constants';
import type { CreateSurveyFormValues } from '../../types/survey.types';
import { SurveyFormSection } from './primitives/SurveyFormSection';

const HEADING_ID = 'survey-basic-info-heading';

export function SurveyBasicInfoSection() {
  const { control } = useFormContext<CreateSurveyFormValues>();

  return (
    <SurveyFormSection ariaLabelledBy={HEADING_ID}>
      <div>
        <h2 id={HEADING_ID} className="text-lg font-semibold">
          Basic Info
        </h2>
      </div>

      <FormField
        control={control}
        name="title"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel>
              Title <span className="text-destructive">*</span>
            </FormLabel>
            <FormControl>
              <Input
                {...field}
                placeholder="e.g., Monthly Field Visit Survey"
                className="h-10"
                maxLength={SURVEY_TITLE_MAX_LENGTH}
                aria-required="true"
                aria-invalid={fieldState.invalid || undefined}
              />
            </FormControl>
            <div className="flex items-center justify-between gap-2">
              <FormMessage />
              <CharacterCounter
                current={field.value?.length ?? 0}
                max={SURVEY_TITLE_MAX_LENGTH}
                className="ml-auto"
              />
            </div>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="description"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel>Description</FormLabel>
            <FormControl>
              <Textarea
                {...field}
                value={field.value ?? ''}
                rows={3}
                placeholder="Short context about this survey."
                maxLength={SURVEY_DESCRIPTION_MAX_LENGTH}
                aria-invalid={fieldState.invalid || undefined}
              />
            </FormControl>
            <div className="flex items-center justify-between gap-2">
              <FormMessage />
              <CharacterCounter
                current={field.value?.length ?? 0}
                max={SURVEY_DESCRIPTION_MAX_LENGTH}
                className="ml-auto"
              />
            </div>
          </FormItem>
        )}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          control={control}
          name="startDate"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>
                Start Date <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <DatePickerInput
                  id="survey-start-date"
                  value={field.value}
                  onChange={field.onChange}
                  className="sm:h-10"
                  {...(fieldState.invalid ? { 'aria-invalid': true } : {})}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="endDate"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>
                End Date <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <DatePickerInput
                  id="survey-end-date"
                  value={field.value}
                  onChange={field.onChange}
                  className="sm:h-10"
                  {...(fieldState.invalid ? { 'aria-invalid': true } : {})}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </SurveyFormSection>
  );
}
